import { dueDay } from "../../utils/due-day";
import {
  Box,
  Card,
  Center,
  Loader,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { ReceiptIcon } from "@phosphor-icons/react";
import { currencyFormat } from "../../utils";
import { dateTimeFormat } from "../../utils/date";
import { InvoiceStatusBadge } from "./invoice-status-badge";
import { InvoiceActionsMenu } from "./invoice-actions-menu";
import type { InvoiceTableProps } from "./types";
import classes from "./invoice-table.module.css";

export function InvoiceTable({
  invoices,
  loading,
  onRowClick,
  onView,
  onEdit,
  onDownload,
  onDelete,
}: InvoiceTableProps) {
  if (loading) {
    return (
      <Card radius="md" withBorder p={0}>
        <Center mih={240}>
          <Loader size="sm" />
        </Center>
      </Card>
    );
  }

  if (!invoices.length) {
    return (
      <Card radius="md" withBorder p={0}>
        <Center mih={320}>
          <Stack align="center" gap="xs">
            <ReceiptIcon size={40} color="var(--mantine-color-gray-6)" />
            <Text size="sm" fw={500} c="gray.9">
              No invoices found
            </Text>
            <Text size="sm" c="gray.7">
              Create a new invoice to get started
            </Text>
          </Stack>
        </Center>
      </Card>
    );
  }

  return (
    <Box className={classes.card}>
      <Table.ScrollContainer minWidth={980}>
        <Table
          className={classes.table}
          withRowBorders={false}
          verticalSpacing={0}
          horizontalSpacing={0}
          tabularNums
          classNames={{ tr: classes.tr }}
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Invoice</Table.Th>
              <Table.Th>Customer</Table.Th>
              <Table.Th ta="right">Total</Table.Th>
              <Table.Th ta="right">Paid</Table.Th>
              <Table.Th ta="right">Balance</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Due Date</Table.Th>
              <Table.Th>Employee</Table.Th>
              <Table.Th className={classes.actionCell} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {invoices.map((inv) => (
              <Table.Tr key={inv.id} onClick={() => onRowClick?.(inv)}>
                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm" fw={500} c="gray.9">
                      {inv.number}
                    </Text>
                    {inv.issueDate && (
                      <Text size="sm" c="gray.7">
                        {dateTimeFormat(inv.issueDate)}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>

                <Table.Td>
                  <Stack gap={2}>
                    <Text size="sm" fw={500} c="gray.9">
                      {inv.customer.name}
                    </Text>
                    {inv.customer.email && (
                      <Text size="sm" c="gray.7">
                        {inv.customer.email}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>

                <Table.Td ta="right">
                  <Text size="sm" fw={500} c="gray.9" className={classes.num}>
                    {currencyFormat(inv.totalAmount, { withSymbol: false })}
                  </Text>
                </Table.Td>

                <Table.Td ta="right">
                  <Text size="sm" fw={500} c="gray.8" className={classes.num}>
                    {currencyFormat(inv.paidAmount, { withSymbol: false })}
                  </Text>
                </Table.Td>

                <Table.Td ta="right">
                  <Text
                    size="sm"
                    fw={500}
                    c={inv.remainingBalance > 0 ? "red.8" : "gray.7"}
                    className={classes.num}
                  >
                    {currencyFormat(inv.remainingBalance, { withSymbol: false })}
                  </Text>
                </Table.Td>

                <Table.Td>
                  <InvoiceStatusBadge status={inv.status} />
                </Table.Td>

                <Table.Td>
                  <Text size="sm" c="gray.8">
                    {dueDay(inv.dueDate) ?? "-"}
                  </Text>
                </Table.Td>

                <Table.Td>
                  <Text size="sm" c="gray.7">
                    {inv.employee ?? "-"}
                  </Text>
                </Table.Td>

                <Table.Td className={classes.actionCell}>
                  <InvoiceActionsMenu
                    onView={() => onView?.(inv)}
                    onEdit={() => onEdit?.(inv)}
                    onDownload={() => onDownload?.(inv)}
                    onDelete={() => onDelete?.(inv)}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Box>
  );
}
